import type { SortDirection } from '@/hooks/use-sort-state';

/**
 * Generische Sortierfunktion fuer Arrays von Objekten.
 * Unterstuetzt string, number und ISO-8601 Date-Strings.
 * null/undefined Werte werden immer ans Ende sortiert.
 * Gibt ein neues Array zurueck (immutable).
 */
export function sortItems<T>(
  items: readonly T[],
  column: keyof T,
  direction: SortDirection,
): T[] {
  return [...items].sort((a, b) => {
    const aVal = a[column];
    const bVal = b[column];

    // null/undefined immer ans Ende
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;

    let comparison = 0;

    if (typeof aVal === 'number' && typeof bVal === 'number') {
      comparison = aVal - bVal;
    } else if (typeof aVal === 'string' && typeof bVal === 'string') {
      // Versuch als ISO-8601 Date zu parsen
      const aDate = Date.parse(aVal);
      const bDate = Date.parse(bVal);

      if (!isNaN(aDate) && !isNaN(bDate) && looksLikeIsoDate(aVal) && looksLikeIsoDate(bVal)) {
        comparison = aDate - bDate;
      } else {
        comparison = aVal.localeCompare(bVal);
      }
    } else {
      comparison = String(aVal).localeCompare(String(bVal));
    }

    return direction === 'asc' ? comparison : -comparison;
  });
}

/**
 * Pruefen ob ein String wie ein ISO-8601 Datum aussieht.
 * Verhindert false positives bei normalen Strings.
 */
function looksLikeIsoDate(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}/.test(value);
}
